import { useEffect, useId, useRef, useState } from 'react';
import { useT } from './LocaleProvider';
import type { ReactNode } from 'react';
import { isApiError } from '../lib/api';
import { secondsLeft } from '../lib/otp-cooldown';
import { IconEye, IconEyeOff, IconHelp, IconInfo } from './icons';

export function Card({ title, subtitle, children, actions, icon, tint, className, ref, headingLevel = 2 }: {
  title?: string; subtitle?: string; children: ReactNode; actions?: ReactNode;
  /** Extra classes on the section — a layout hook, not a second card style. */
  className?: string;
  /**
   * A glyph for the card's topic, on a tinted rounded square beside the title.
   *
   * Optional, and absent by default: every card in the admin console inherits
   * this component, and a badge on all of them is the flat page again in a new
   * coat. Pass it where the topic is worth signalling at a glance.
   */
  icon?: ReactNode;
  /** Which pastel pair the badge takes. Only meaningful with `icon`. */
  tint?: 'money' | 'result' | 'work' | 'plan' | 'alert' | 'time';
  /** React 19 passes ref as an ordinary prop — no forwardRef wrapper needed. */
  ref?: React.Ref<HTMLElement>;
  /**
   * 1 when the card IS the page — a scorecard, a support thread — so the
   * document still has one top-level heading. Styled identically to 2.
   */
  headingLevel?: 1 | 2;
}) {
  /*
   * Badge, then the title with its subtitle UNDER it, then the action.
   *
   * The subtitle used to sit below the whole head, starting at the card's left
   * edge — so on a card with a badge it began 40px to the left of the title it
   * belongs to, and read as a caption for the card rather than as the second
   * line of its heading. Inside the text column the two align, which is what
   * makes a head one object instead of three.
   *
   * `.card-headings` takes the room and shrinks (`min-width: 0` in the
   * stylesheet), so a long title pushes the action to the edge rather than off
   * it, and a card with no badge and no action lays out identically.
   */
  const head = (title || actions || subtitle) && (
    <div className="card-head">
      {icon && <span className={`card-badge tint-${tint ?? 'plan'}`}>{icon}</span>}
      <div className="card-headings">
        {title && (headingLevel === 1 ? <h1>{title}</h1> : <h2>{title}</h2>)}
        {subtitle && <p className="card-sub">{subtitle}</p>}
      </div>
      {actions}
    </div>
  );

  return (
    <section className={className ? `card ${className}` : 'card'} ref={ref}>
      {head}
      {children}
    </section>
  );
}

export const Pill = ({ tone = 'neutral', children }: {
  tone?: 'ok' | 'warn' | 'bad' | 'neutral'; children: ReactNode;
}) => <span className={`pill ${tone}`}>{children}</span>;

export const Notice = ({ tone = 'info', children }: {
  tone?: 'info' | 'ok' | 'warn' | 'bad'; children: ReactNode;
}) => <div className={`notice ${tone}`}>{children}</div>;

/**
 * `role="status"` with real text, not `aria-label` on a bare div: a label on an
 * element with no role is ignored by screen readers, so every loading state in
 * the product was silent to anybody who could not see the ring turning.
 */
export function Spinner() {
  const t = useT();
  return (
    <div className="center-screen" role="status">
      <div className="spinner" aria-hidden="true" />
      <span className="sr-only">{t('common.loading')}</span>
    </div>
  );
}

/**
 * Error display. A permission refusal is not a fault — it is the system working
 * — so it reads as information rather than as something broken.
 *
 * `role="alert"`, because these arrive after a click, under a form: a sighted
 * person watches the box appear and somebody using a screen reader heard
 * nothing at all.
 *
 * WHAT IS NOT SHOWN. An `ApiError` carries a sentence the server wrote for a
 * person, and so does an `Error` a component builds itself ("Could not build
 * the PDF"). A `TypeError` is a fault in this code — "Cannot read properties of
 * undefined" — which tells a parent nothing they can act on. Those go to the
 * console for whoever is debugging, and the screen says what to do instead.
 */
const FAULTS = [TypeError, SyntaxError, ReferenceError, RangeError];

/** The words for an error: its own sentence, or the generic one for a fault. */
function errorText(error: unknown, t: ReturnType<typeof useT>): string {
  const fault = FAULTS.some((F) => error instanceof F);
  if (fault || !(error instanceof Error)) console.error('[ui] unexpected error', error);
  return fault || !(error instanceof Error) ? t('common.error_load') : error.message;
}

export function ErrorBox({ error }: { error: unknown }) {
  const t = useT();
  if (!error) return null;
  const denied = isApiError(error) && (error.status === 403 || error.status === 401);
  return (
    <div className={`notice ${denied ? 'warn' : 'bad'}`} role="alert">
      {errorText(error, t)}
    </div>
  );
}

/**
 * An error under the one field it is about, rather than in a box at the top.
 *
 * A form long enough to scroll hid its own refusals: the button is at the
 * bottom, the box was at the top, and the person who pressed it saw nothing
 * happen. `lib/field-errors.ts` decides which field an error belongs to; this
 * only draws it there. `<small>`, so it is phrasing content and valid anywhere
 * a field puts it, including inside a wrapping `<label>`.
 */
export function FieldError({ error }: { error: unknown }) {
  const t = useT();
  if (!error) return null;
  return <small className="field-error" role="alert">{errorText(error, t)}</small>;
}

/**
 * Nothing to show, said as a sentence on a quiet ground of its own.
 *
 * It was a line of muted text with a margin, which on a white card read as a
 * caption for whatever sat above it rather than as the answer to "what is in
 * this list". `role="status"` so a filter that empties a list says so aloud.
 */
export function EmptyState({ children }: { children: ReactNode }) {
  return <p className="empty-state" role="status">{children}</p>;
}

/**
 * What an error boundary shows in place of a screen that could not render.
 *
 * Reload, not "try again". The commonest cause in production is a deploy that
 * lands while a tab is open: the lazily loaded screen it asks for has a new
 * file name and the old file is gone, so re-rendering asks for the same
 * missing file. Only a reload fetches the new build.
 */
export function CrashNotice() {
  const t = useT();
  return (
    <div className="crash" role="alert">
      <h2>{t('common.error_title')}</h2>
      <p className="muted">{t('common.error_body')}</p>
      <button className="primary" onClick={() => window.location.reload()}>
        {t('common.reload')}
      </button>
    </div>
  );
}

/**
 * A "?" beside a field label, holding the sentence that used to sit under the
 * input. Render it INSIDE a `.field-with-tip` wrapper, after the <label>, and
 * give the input `aria-describedby={id}`.
 *
 * Kept out of the <label> on purpose. Everything inside a label becomes the
 * field's accessible name, so a button and a paragraph in there would have a
 * screen reader announce "The year starts, about the start date, Papers land a
 * week apart…" as the NAME of a date box. The input describes itself with the
 * text instead, which is what a description is for, and the text is read
 * whether or not it is showing.
 *
 * Opened by hover and by keyboard focus through CSS, and ALSO toggled by a
 * click: iOS Safari does not focus a button on tap, so hover and focus alone
 * would never show it on a phone. Blur closes it again, so a mouse user who
 * clicked and moved on is not left with a stray popover.
 */
export function InfoTip({ id, label, children, glyph = <IconHelp /> }: {
  /** The tooltip's element id — what the input's `aria-describedby` names. */
  id: string;
  /** The icon's accessible name, e.g. "About the start date". */
  label: string;
  children: ReactNode;
  /** The "?" by default; a form field's hint takes the "i" (see `TipField`). */
  glyph?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLSpanElement>(null);

  // A tap anywhere else closes it. Blur alone is not enough: on iOS Safari a
  // tap never focuses the button, so there is no blur to come, and the tip
  // would sit open until the icon was tapped a second time.
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('click', away);
    return () => document.removeEventListener('click', away);
  }, [open]);

  return (
    <>
      <span ref={wrap} className={`field-tip${open ? ' open' : ''}`}>
        <button
          type="button"
          className="field-tip-btn"
          aria-label={label}
          aria-describedby={id}
          onClick={() => setOpen((o) => !o)}
          onBlur={() => setOpen(false)}
        >
          {glyph}
        </button>
      </span>
      <span role="tooltip" id={id}>{children}</span>
    </>
  );
}

/**
 * A labelled field whose hint sits behind an "i" beside the label, not under
 * the box.
 *
 * WHY NOT UNDER THE BOX. The sign-up form's hints were coloured sentences under
 * each input, and in orange they read as errors — the one thing a hint must
 * never look like, on a form that now puts real errors in exactly that place.
 * Behind an "i" they are one tap away and the space under a box belongs to the
 * error alone.
 *
 * WHY THE LABEL DOES NOT WRAP THE CONTROL HERE, when `label.field` elsewhere
 * does. `InfoTip` must sit OUTSIDE the <label>, or its button and text become
 * part of the field's accessible name (see its note). So the label names the
 * control by `for`, the icon sits beside it in `.field-head`, and the control
 * is DESCRIBED by the tip's text — read out on focus whether or not it is open.
 *
 * The control is a render prop so its props carry the ids: spread them onto
 * the input or select, `{(c) => <input {...c} … />}`.
 */
export function TipField({ label, tip, children, className }: {
  label: string;
  /** The hint. Absent: an ordinary field, same markup, no icon. */
  tip?: ReactNode;
  children: (control: { id: string; 'aria-describedby'?: string }) => ReactNode;
  className?: string;
}) {
  const t = useT();
  const id = useId();
  const tipId = useId();
  return (
    <div className={className ? `field ${className}` : 'field'}>
      <div className="field-head">
        <label htmlFor={id}>{label}</label>
        {tip && (
          <InfoTip id={tipId} label={t('field.more_about', { field: label })} glyph={<IconInfo />}>
            {tip}
          </InfoTip>
        )}
      </div>
      {children(tip ? { id, 'aria-describedby': tipId } : { id })}
    </div>
  );
}

/**
 * A password box with an eye that reveals what was typed.
 *
 * One component rather than the same markup in three files, because the parts
 * that are easy to get wrong are invisible once they are right:
 *
 *   type="button" — a <button> inside a <form> DEFAULTS TO SUBMIT. The sign-in
 *   field sits in one, so an eye without this signs you in (or fails to) the
 *   moment you tap it to check your own typing. The one bug this component
 *   exists to make unrepeatable.
 *
 *   autoCapitalize / autoCorrect / spellCheck — a revealed field is
 *   `type="text"`, and a phone keyboard treats text as prose: it capitalises
 *   the first letter and autocorrects the rest. A parent who reveals their
 *   password to check it and then keeps typing gets a different password than
 *   the one on screen, and no error explains why the sign-in failed.
 *
 * The eye is positioned over the input rather than sitting beside it in a flex
 * row, which keeps the input an ordinary `width: 100%` block and so steps
 * around the min-width trap documented at the top of the layout section.
 *
 * Worth saying plainly: revealing is a convenience for the person typing, not
 * a security decision. It shows the password to anyone who can see the screen,
 * which is why it starts hidden and is never remembered between visits.
 */
export function PasswordField({
  label, value, onChange, autoComplete, required = false, hint, icon, placeholder, error,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /**
   * Required, not defaulted. 'current-password' on a sign-in box and
   * 'new-password' on one that creates an account — a browser that fills a
   * saved password into the account somebody is creating for their child has
   * made a mess only a support call untangles.
   */
  autoComplete: 'current-password' | 'new-password';
  required?: boolean;
  hint?: string;
  /**
   * A leading glyph inside the field, so the password box matches the icon
   * inputs beside it on the sign-up form. Optional and absent by default —
   * login and forgot-password keep their plain field and are untouched.
   */
  icon?: ReactNode;
  /** A refusal about THIS password, drawn under it — see `FieldError`. */
  error?: unknown;
}) {
  const t = useT();
  const [shown, setShown] = useState(false);
  const action = t(shown ? 'auth.password_hide' : 'auth.password_show');

  /*
   * The hint behind an "i" beside the label, like every field on the auth
   * pages — which is also why this is no longer a wrapping <label>: the eye
   * button used to sit INSIDE it, so "Show password" was part of the password
   * box's accessible name. Named by `for` now, and described by the hint.
   */
  return (
    <TipField label={label} tip={hint}>
      {(control) => (
        <>
          <span className={icon ? 'pw pw-icon' : 'pw'}>
            {icon && <span className="pw-lead" aria-hidden="true">{icon}</span>}
            <input
              {...control}
              type={shown ? 'text' : 'password'}
              value={value}
              placeholder={placeholder}
              autoComplete={autoComplete}
              required={required}
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              onChange={(e) => onChange(e.target.value)}
            />
            {/*
              * The accessible name carries the state — "Show password" becomes
              * "Hide password" — rather than a static name plus `aria-pressed`.
              * Both are valid; announcing one thing instead of "Hide password,
              * pressed" is the shorter sentence to hear mid-form.
              *
              * `tabIndex={-1}` is deliberately NOT set: somebody who cannot see
              * the screen still has a reason to reach this, and a control that
              * cannot be tabbed to is a control that does not exist for them.
              */}
            <button
              type="button"
              className="pw-reveal"
              aria-label={action}
              title={action}
              onClick={() => setShown((s) => !s)}
            >
              {shown ? <IconEyeOff /> : <IconEye />}
            </button>
          </span>
          {/* The refusal under the box it is about. */}
          <FieldError error={error} />
        </>
      )}
    </TipField>
  );
}

/**
 * A modal confirmation, on the native <dialog> element.
 *
 * Native rather than a hand-rolled overlay because the browser already does
 * the hard parts correctly: focus is trapped, Escape closes, the rest of the
 * page is inert, and it is announced to a screen reader as a dialog. Every
 * one of those is a thing a div gets wrong quietly.
 */
export function ConfirmDialog({
  open, title, body, confirmLabel, cancelLabel, tone = 'primary', onConfirm, onCancel,
}: {
  open: boolean;
  title: string;
  body: ReactNode;
  confirmLabel?: string;
  /** Defaults to the translated "Cancel". */
  cancelLabel?: string;
  tone?: 'primary' | 'danger';
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const t = useT();
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      className="confirm"
      // Escape and the backdrop both mean "no". Without this the dialog closes
      // natively while React still believes it is open, and it never reopens.
      onCancel={(e) => { e.preventDefault(); onCancel(); }}
      onClick={(e) => { if (e.target === ref.current) onCancel(); }}
    >
      <h2>{title}</h2>
      <div className="muted" style={{ margin: '.5rem 0 1rem' }}>{body}</div>
      <div className="btn-row" style={{ justifyContent: 'flex-end' }}>
        <button className="ghost" onClick={onCancel}>{cancelLabel ?? t('common.cancel')}</button>
        <button className={tone} onClick={onConfirm} autoFocus>{confirmLabel ?? t('common.confirm')}</button>
      </div>
    </dialog>
  );
}

/**
 * A modal that asks for a line of text, on the same native <dialog>.
 *
 * Exists because `window.prompt` has the `window.confirm` failure mode this
 * codebase already banned: when the browser has stopped showing dialogs it
 * returns null instantly, and the action it was guarding silently dies — a
 * reviewer clicks "Send back", nothing happens, and there is no error to
 * report. The rule for reasons is decided by the CALLER via `required`:
 * a mandatory reason keeps its button disabled rather than failing after.
 */
export function PromptDialog({
  open, title, body, label, placeholder, confirmLabel = 'Save',
  required = true, onSubmit, onCancel,
}: {
  open: boolean;
  title: string;
  body?: ReactNode;
  label: string;
  placeholder?: string;
  confirmLabel?: string;
  required?: boolean;
  onSubmit: (value: string) => void;
  onCancel: () => void;
}) {
  const t = useT();
  const ref = useRef<HTMLDialogElement>(null);
  const [value, setValue] = useState('');

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) { setValue(''); el.showModal(); }
    if (!open && el.open) el.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      className="confirm"
      onCancel={(e) => { e.preventDefault(); onCancel(); }}
      onClick={(e) => { if (e.target === ref.current) onCancel(); }}
    >
      <h2>{title}</h2>
      {body && <div className="muted" style={{ margin: '.5rem 0 .75rem' }}>{body}</div>}
      <label className="field" style={{ display: 'block', marginBottom: '1rem' }}>
        {label}
        <textarea
          rows={3}
          value={value}
          placeholder={placeholder}
          onChange={(e) => setValue(e.target.value)}
          style={{ width: '100%', marginTop: '.3rem' }}
          autoFocus
        />
      </label>
      <div className="btn-row" style={{ justifyContent: 'flex-end' }}>
        <button className="ghost" onClick={onCancel}>{t('common.cancel')}</button>
        <button
          className="primary"
          disabled={required && !value.trim()}
          onClick={() => onSubmit(value.trim())}
        >
          {confirmLabel ?? t('common.confirm')}
        </button>
      </div>
    </dialog>
  );
}

/**
 * Bring a panel into view when it appears.
 *
 * Forms that open in response to a button are placed above their list, which
 * fixes "the button did nothing" — but it does not fix "Edit on row forty
 * opened something off the top of the screen". Anything that opens because
 * somebody asked for it should be looked at, wherever they were standing.
 *
 * `nearest` rather than `start`: if the panel is already visible this does
 * nothing at all, so opening a form at the top of a short page does not jerk
 * the page around for no reason.
 */
export function useRevealOnOpen<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  useEffect(() => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, []);
  return ref;
}

/**
 * One honest line when the browser says the connection has gone.
 *
 * Deliberately vague about consequences, because this renders on every screen
 * and the consequence differs: the exam runner already says something specific
 * about unsaved answers through the outbox, and duplicating that here would
 * either contradict it or repeat it. `navigator.onLine` only proves there is no
 * local link — a captive portal still reports true — so this is worded as what
 * we actually know rather than as a promise about the network.
 */
export function OfflineBar() {
  const [offline, setOffline] = useState(() => typeof navigator !== 'undefined' && !navigator.onLine);

  useEffect(() => {
    const back = () => setOffline(false);
    const gone = () => setOffline(true);
    window.addEventListener('online', back);
    window.addEventListener('offline', gone);
    return () => {
      window.removeEventListener('online', back);
      window.removeEventListener('offline', gone);
    };
  }, []);

  if (!offline) return null;

  return (
    <div className="offline-bar" role="status" aria-live="polite">
      You are offline. Anything new will not load until your connection returns.
    </div>
  );
}

/**
 * A resend button that disables itself for a cooldown and counts down.
 *
 * `cooldownUntil` (epoch ms, or null) is owned by the parent — it sets it when a
 * code goes out, seeded from the server's `retryAfterSeconds`. This component
 * only ticks a clock and renders; the pure maths is in `lib/otp-cooldown`. The
 * server enforces the same wait, so this is UX, not the guard.
 */
export function ResendButton({
  cooldownUntil, busy, onClick, idleLabel, countdownLabel, className = 'ghost', hiddenSuffix,
}: {
  cooldownUntil: number | null;
  busy?: boolean;
  onClick: () => void;
  idleLabel: string;
  countdownLabel: (seconds: number) => string;
  className?: string;
  /**
   * Heard, not seen: two "Send code" buttons on one form are the same button
   * to a screen reader. Appended as hidden text rather than set as an
   * aria-label, so the countdown the button shows is still what is announced.
   */
  hiddenSuffix?: string;
}) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (cooldownUntil === null) return undefined;
    setNow(Date.now());
    const id = setInterval(() => {
      const t = Date.now();
      setNow(t);
      if (t >= cooldownUntil) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, [cooldownUntil]);

  const left = secondsLeft(cooldownUntil, now);
  return (
    <button
      type="button"
      className={className}
      disabled={!!busy || left > 0}
      onClick={onClick}
    >
      {left > 0 ? countdownLabel(left) : idleLabel}
      {hiddenSuffix && <span className="sr-only"> {hiddenSuffix}</span>}
    </button>
  );
}
